if (!customElements.get('bundle-builder')) {
  customElements.define(
    'bundle-builder',
    class BundleBuilder extends HTMLElement {
      connectedCallback() {
        this.maxItems = this.dataset.maxItems ? Number(this.dataset.maxItems) : Infinity;
        this.cards = Array.from(this.querySelectorAll('[data-bundle-card]'));
        this.originalValueTexts = Array.from(this.querySelectorAll('[data-bundle-original-value]'));
        this.discountedValueTexts = Array.from(this.querySelectorAll('[data-bundle-discounted-value]'));
        this.discountStepGroups = Array.from(this.querySelectorAll('.bundle-discounts'));
        this.selectedGrids = Array.from(this.querySelectorAll('[data-bundle-selected-grid]'));
        this.statuses = Array.from(this.querySelectorAll('[data-bundle-status]'));
        this.addButtons = Array.from(this.querySelectorAll('[data-bundle-add]'));
        this.deleteIconTemplate = this.querySelector('[data-bundle-delete-icon]');
        this.filterButtons = Array.from(this.querySelectorAll('[data-toughness-filter]'));
        this.activeToughnessFilter = 'all';

        this.cards.forEach((card) => {
          const button = card.querySelector('[data-bundle-toggle]');
          button?.addEventListener('click', () => this.toggleCard(card));

          const variantInputs = Array.from(card.querySelectorAll('[data-bundle-variant]'));
          variantInputs.forEach((input) => {
            input.addEventListener('change', () => {
              // The shopper explicitly picked this colour: keep it selected even when
              // it is sold out, so the colour name stays visible and the toggle button
              // can say "אזל מהמלאי" instead of silently bouncing to another colour.
              card.dataset.userPicked = 'true';
              this.selectVariant(card, input);
            });
          });

          const checkedInput = variantInputs.find((input) => input.checked) || variantInputs[0];
          if (checkedInput) {
            checkedInput.checked = true;
            this.selectVariant(card, checkedInput, { shouldRender: false });
          }
        });

        this.filterButtons.forEach((button) => {
          button.addEventListener('click', () => this.setToughnessFilter(button.dataset.toughnessFilter || 'all'));
        });

        this.addButtons.forEach((button) => {
          button.addEventListener('click', () => this.addBundleToCart());
        });
        this.selectedGrids.forEach((grid) => {
          grid.addEventListener('click', (event) => {
            const target = event.target instanceof Element ? event.target : null;
            const removeButton = target?.closest('[data-bundle-remove]');

            if (!removeButton) return;

            this.removeCardByProductId(removeButton.dataset.productId);
          });
        });

        this.render();
        this.applyToughnessFilter();

        // Sticky/fixed header behavior
        this.stickyHeader = this.querySelector('[data-bundle-sticky-header]');
        this.summary = this.querySelector('.bundle-builder__summary');
        this.selectedGrid = this.summary?.querySelector('[data-bundle-selected-grid]');

        if (this.stickyHeader && this.selectedGrid && this.summary) {
          const updateStickyHeaderPosition = () => {
            const rect = this.summary.getBoundingClientRect();
            this.stickyHeader.style.left = `${rect.left}px`;
            this.stickyHeader.style.width = `${rect.width}px`;
          };

          updateStickyHeaderPosition();
          window.addEventListener('resize', updateStickyHeaderPosition);

          const isMobile = () => window.innerWidth < 990;

          const handleScroll = () => {
            const gridRect = this.selectedGrid.getBoundingClientRect();
            const gridIsVisible = gridRect.top < window.innerHeight;

            if (gridIsVisible && this.stickyHeader.classList.contains('is-fixed')) {
              this.stickyHeader.classList.remove('is-fixed');
              this.summary.classList.remove('has-fixed-header');
              this.stickyHeader.style.left = '';
              this.stickyHeader.style.width = '';
              this.summary.style.removeProperty('--bundle-sticky-header-height');
            } else if (!gridIsVisible && !this.stickyHeader.classList.contains('is-fixed')) {
              const headerHeight = this.stickyHeader.offsetHeight;
              this.stickyHeader.classList.add('is-fixed');
              this.summary.style.setProperty('--bundle-sticky-header-height', `${headerHeight}px`);
              this.summary.classList.add('has-fixed-header');
              updateStickyHeaderPosition();
            }
          };

          // Only enable mobile fixed behavior on small screens; on desktop the whole summary is sticky via CSS
          if (isMobile()) {
            window.addEventListener('scroll', handleScroll, { passive: true });
            // run once to set initial state
            handleScroll();
          }

          // Keep behavior responsive on resize: enable/disable mobile behavior and clear mobile state on desktop
          window.addEventListener('resize', () => {
            updateStickyHeaderPosition();
            if (!isMobile()) {
              // clear any mobile fixed state
              this.stickyHeader.classList.remove('is-fixed');
              this.summary.classList.remove('has-fixed-header');
              this.stickyHeader.style.left = '';
              this.stickyHeader.style.width = '';
              this.summary.style.removeProperty('--bundle-sticky-header-height');
            } else {
              // ensure mobile logic runs when switching back
              handleScroll();
            }
          });

          // Tapping the fixed bar scrolls down so the summary block (grid + add button)
          // lands right beneath the sticky header's normal, docked position.
          this.stickyHeader.addEventListener('click', () => {
            if (!this.stickyHeader.classList.contains('is-fixed')) return;

            const headerHeight = this.stickyHeader.offsetHeight;
            const summaryTop = this.summary.getBoundingClientRect().top + window.scrollY;
            const targetScrollTop = summaryTop - headerHeight;

            window.scrollTo({
              top: targetScrollTop,
              behavior: 'smooth',
            });
          });
        }
      }

      get selectedCards() {
        return this.cards.filter((card) => card.dataset.selected === 'true');
      }

      toggleCard(card) {
        const isSelected = card.dataset.selected === 'true';
        const selectedCount = this.selectedCards.length;

        if (!isSelected && selectedCount >= this.maxItems) {
          this.setStatus(`You can select up to ${this.maxItems} products.`);
          return;
        }

        card.dataset.selected = isSelected ? 'false' : 'true';
        this.setStatus('');
        this.render();
      }

      removeCardByProductId(productId) {
        const card = this.cards.find((item) => item.dataset.productId === productId);

        if (!card) return;

        card.dataset.selected = 'false';
        this.setStatus('');
        this.render();
      }

      selectVariant(card, input, options = {}) {
        if (!card || !input) return;

        const selectedColor = card.querySelector('[data-bundle-selected-color]');
        const selectedToughness = card.querySelector('[data-bundle-selected-toughness]');
        const cardImage = card.querySelector('.bundle-builder__image');

        card.dataset.variantId = input.dataset.variantId || card.dataset.variantId || '';
        card.dataset.variantPrice = input.dataset.variantPrice || card.dataset.variantPrice || '0';
        card.dataset.variantAvailable = input.dataset.variantAvailable === 'false' ? 'false' : 'true';

        if (card.dataset.variantAvailable === 'false') {
          card.dataset.selected = 'false';
        }

        if (input.dataset.variantImage) {
          card.dataset.productImage = input.dataset.variantImage;
        }

        if (selectedColor) {
          selectedColor.textContent = input.dataset.variantTitle || input.value || '';
        }

        if (selectedToughness) {
          const toughness = input.dataset.toughness || '';
          const selectedToughnessValue = selectedToughness.querySelector('[data-bundle-selected-toughness-value]');

          if (selectedToughnessValue) {
            selectedToughnessValue.textContent = toughness;
          }

          selectedToughness.hidden = !toughness || toughness === 'תוספות';
        }

        if (cardImage && input.dataset.variantImageLarge) {
          cardImage.src = input.dataset.variantImageLarge;
          cardImage.srcset = '';
        }

        if (options.shouldRender !== false) {
          this.applyToughnessFilter();
          this.render();
        }
      }

      setToughnessFilter(filter) {
        this.activeToughnessFilter = filter || 'all';

        this.filterButtons.forEach((button) => {
          const isActive = this.isActiveToughnessButton(button);
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });

        this.applyToughnessFilter();
        this.render();
      }

      applyToughnessFilter() {
        this.cards.forEach((card) => {
          const swatches = Array.from(card.querySelectorAll('[data-bundle-variant]'));
          if (swatches.length === 0) return;

          let firstMatchingInput = null;
          let firstAvailableInput = null;

          swatches.forEach((input) => {
            const label = input.closest('.bundle-builder__swatch-label');
            const matchesFilter = this.matchesToughness(input.dataset.toughness, input.dataset.toughnessKey);
            const isAvailable = this.isVariantAvailable(input);

            if (label) {
              label.hidden = !matchesFilter;
            }

            if (matchesFilter && !firstMatchingInput) {
              firstMatchingInput = input;
            }

            if (matchesFilter && isAvailable && !firstAvailableInput) {
              firstAvailableInput = input;
            }
          });

          const checkedInput = swatches.find((input) => input.checked);
          // A colour the shopper picked on purpose stays selected even when it is sold out.
          const keepSoldOutPick = card.dataset.userPicked === 'true';
          const selectedStillVisible =
            checkedInput &&
            this.matchesToughness(checkedInput.dataset.toughness, checkedInput.dataset.toughnessKey) &&
            (this.isVariantAvailable(checkedInput) || keepSoldOutPick);
          const selectedInput = selectedStillVisible ? checkedInput : firstAvailableInput;

          if (selectedInput) {
            selectedInput.checked = true;
            this.selectVariant(card, selectedInput, { shouldRender: false });
          } else if (firstMatchingInput) {
            firstMatchingInput.checked = true;
            this.selectVariant(card, firstMatchingInput, { shouldRender: false });
          }

          const hasAvailableMatchingToughness = Boolean(firstAvailableInput);

          card.hidden = !hasAvailableMatchingToughness;
          card.toggleAttribute('data-no-matching-toughness', !hasAvailableMatchingToughness);

          if (!hasAvailableMatchingToughness) {
            card.dataset.selected = 'false';
          }
        });
      }

      isVariantAvailable(input) {
        return input.dataset.variantAvailable !== 'false';
      }

      isActiveToughnessButton(button) {
        const activeFilter = this.normalizeToughness(this.activeToughnessFilter);

        return [button.dataset.toughnessFilter, button.dataset.toughnessFilterKey].some(
          (item) => this.normalizeToughness(item) === activeFilter
        );
      }

      matchesToughness(value, key) {
        if (this.activeToughnessFilter === 'all') return true;

        const activeFilter = this.normalizeToughness(this.activeToughnessFilter);

        return [value, key].some((item) => this.normalizeToughness(item) === activeFilter);
      }

      normalizeToughness(value) {
        return String(value || '').trim().toLowerCase();
      }

      render() {
        const selectedCards = this.selectedCards;
        const selectedCount = selectedCards.length;
        const discount = this.getDiscount(selectedCount);
        const originalValue = selectedCards.reduce((total, card) => total + Number(card.dataset.variantPrice || 0), 0);
        const discountedValue = Math.round(originalValue * ((100 - discount) / 100));

        this.originalValueTexts.forEach((text) => {
          text.hidden = selectedCount === 0;
          text.textContent = this.formatMoney(originalValue);
          text.classList.toggle('is-discounted', discount > 0);
        });

        this.discountedValueTexts.forEach((text) => {
          text.hidden = discount === 0;
          text.textContent = this.formatMoney(discountedValue);
        });

        this.discountStepGroups.forEach((group) => {
          const steps = Array.from(group.querySelectorAll('[data-bundle-discount-step]'));
          steps.forEach((step, index) => {
            step.classList.toggle('selected', index < selectedCount);
          });
        });

        const isLoading = this.addButtons.some((button) => button.dataset.loading === 'true');

        this.addButtons.forEach((button) => {
          button.disabled = selectedCount === 0 || isLoading;
          button.textContent = isLoading ? 'בטיפול...' : 'הוספת הסט לסל';
        });

        this.selectedGrids.forEach((grid) => {
          const minSlots = 8;
          const rowSize = 4;
          const totalSlots = Math.max(minSlots, Math.ceil((selectedCount + 1) / rowSize) * rowSize);

          let selectedSlots = Array.from(grid.querySelectorAll('[data-bundle-slot]'));

          while (selectedSlots.length < totalSlots) {
            const slot = document.createElement('div');
            slot.setAttribute('data-bundle-slot', '');
            slot.className = 'bundle-builder__selected-slot';
            grid.appendChild(slot);
            selectedSlots.push(slot);
          }

          while (selectedSlots.length > totalSlots) {
            selectedSlots.pop().remove();
          }

          selectedSlots.forEach((slot, index) => {
            const card = selectedCards[index];

            slot.innerHTML = '';
            slot.classList.toggle('bundle-builder__selected-slot--filled', Boolean(card));

            if (!card) {
              const placeholder = document.createElement('span');
              placeholder.className = 'bundle-builder__slot-placeholder';
              placeholder.textContent = String(index + 1);
              slot.appendChild(placeholder);
              return;
            }

            const removeButton = document.createElement('button');
            removeButton.className = 'bundle-builder__remove-button';
            removeButton.type = 'button';
            removeButton.dataset.bundleRemove = '';
            removeButton.dataset.productId = card.dataset.productId;
            removeButton.setAttribute('aria-label', `Remove ${card.dataset.productTitle || 'product'} from bundle`);
            this.renderDeleteIcon(removeButton);

            if (card.dataset.productImage) {
              const image = document.createElement('img');
              image.className = 'bundle-builder__selected-image';
              image.alt = card.dataset.productTitle || '';
              image.src = card.dataset.productImage;
              image.loading = 'lazy';
              slot.append(removeButton, image);
            } else {
              const placeholder = document.createElement('span');
              placeholder.className = 'bundle-builder__slot-placeholder';
              placeholder.textContent = card.dataset.productTitle?.charAt(0) || '';
              slot.append(removeButton, placeholder);
            }
          });
        });

        this.cards.forEach((card) => {
          const isSelected = card.dataset.selected === 'true';
          const button = card.querySelector('[data-bundle-toggle]');
          const maxReached = selectedCount >= this.maxItems && !isSelected;
          const selectedVariantAvailable = card.dataset.variantAvailable !== 'false';

          card.classList.toggle('bundle-builder__product-card--selected', isSelected);
          card.toggleAttribute('data-max-reached', maxReached);
          card.toggleAttribute('data-variant-sold-out', !selectedVariantAvailable);

          if (button) {
            button.disabled = maxReached || !selectedVariantAvailable;
            button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
            button.classList.toggle('add-to-cart-button', !isSelected);
            button.classList.toggle('button-outline', isSelected);
            button.setAttribute(
              'aria-label',
              !selectedVariantAvailable
                ? `${card.dataset.productTitle || 'product'} is out of stock`
                : isSelected
                  ? `Remove ${card.dataset.productTitle || 'product'} from bundle`
                  : `Add ${card.dataset.productTitle || 'product'} to bundle`
            );

            button.textContent = !selectedVariantAvailable ? 'אזל מהמלאי' : isSelected ? 'הסרה' : 'הוספה';
          }
        });
      }

      getDiscount(selectedCount) {
        if (selectedCount >= 8) return 25;
        if (selectedCount >= 6) return 20;
        if (selectedCount >= 4) return 10;
        if (selectedCount >= 2) return 5;

        return 0;
      }

      renderDeleteIcon(element) {
        element.replaceChildren();

        if (!this.deleteIconTemplate) return;

        element.appendChild(this.deleteIconTemplate.content.cloneNode(true));
      }

      setStatus(message) {
        this.statuses.forEach((status) => {
          status.textContent = message;
        });
      }

      formatMoney(cents) {
        const currency = window.Shopify?.currency?.active || this.dataset.currency || 'USD';
        const amount = cents / 100;

        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency,
          minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
          maximumFractionDigits: 2,
        }).format(amount);
      }

      async addBundleToCart() {
        const selectedCards = this.selectedCards;

        if (selectedCards.length === 0 || this.addButtons.length === 0) return;

        this.addButtons.forEach((button) => {
          button.dataset.loading = 'true';
        });
        this.setStatus('');
        this.render();

        const items = selectedCards.map((card) => ({
          id: Number(card.dataset.variantId),
          quantity: 1,
        }));

        const cartItemsComponents = document.querySelectorAll('cart-items-component');
        const sectionIds = Array.from(cartItemsComponents)
          .map((item) => item instanceof HTMLElement && item.dataset.sectionId)
          .filter(Boolean);

        try {
          const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              items,
              sections: sectionIds.join(','),
              sections_url: window.location.pathname,
            }),
          });

          const addResponse = await response.json();

          if (!response.ok || addResponse.status) {
            throw new Error(addResponse.description || addResponse.message || 'Unable to add bundle to cart.');
          }

          const cartResponse = await fetch('/cart.js', {
            headers: {
              Accept: 'application/json',
            },
          });
          const cart = await cartResponse.json();

          document.dispatchEvent(
            new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: addResponse,
                sourceId: 'bundle-builder',
                data: {
                  source: 'bundle-builder',
                  itemCount: cart.item_count,
                  sections: addResponse.sections,
                },
              },
            })
          );

          this.setStatus('Bundle added to cart.');
        } catch (error) {
          console.error(error);
          this.setStatus(error.message || 'Unable to add bundle to cart.');
        } finally {
          this.addButtons.forEach((button) => {
            button.dataset.loading = 'false';
          });
          this.render();
        }
      }
    }
  );
}
